/**
 * Pagination entre les journées — le contenu **suit le doigt**, et la journée
 * voisine entre en même temps.
 *
 * Remplace la détection de geste « tout ou rien » de la première version : on ne
 * décidait qu'au relâchement, et rien ne bougeait pendant le glissement. Ici
 * l'écran est manipulé directement, comme on tourne une page.
 *
 * ⚠️ **Le geste de retour natif iOS n'est jamais intercepté** (§6.1 : « ne jamais
 * intercepter le geste »). Safari le déclenche depuis le bord gauche : tout
 * glissement qui *commence* dans cette bande est ignoré, sans quoi revenir en
 * arrière deviendrait impossible. C'est la seule zone de la page qui ne pagine
 * pas — le reste de la surface entre l'en-tête et la barre d'onglets répond.
 *
 * ⚠️ **Le défilement vertical prime.** L'axe est verrouillé à la première
 * dizaine de pixels et ne change plus ensuite : un geste parti vers le bas reste
 * un défilement jusqu'à son terme, même s'il dérive latéralement. Voler un
 * défilement est bien pire que rater un changement de jour. C'est aussi pour
 * cela que la piste porte `touch-action: pan-y` : le navigateur garde le
 * défilement vertical natif et ne nous laisse que l'horizontale.
 *
 * ⚠️ `PANE_GAP_PX` est la **seule** source de vérité de la gouttière entre deux
 * journées : la course de validation en dépend, et les panneaux voisins sont
 * positionnés à partir d'elle. Ne pas la redéclarer en CSS.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

/** Bande réservée au geste de retour de Safari, en pixels depuis le bord gauche. */
const EDGE_GUARD_PX = 28;

/** Course au-delà de laquelle l'axe du geste est tranché, une fois pour toutes. */
const AXIS_LOCK_PX = 10;

/** À partir d'ici, l'utilisateur regarde vraiment la journée voisine : on la charge. */
const PEEK_PX = 12;

/** Part de la largeur au-delà de laquelle le relâchement bascule de journée. */
const COMMIT_RATIO = 0.28;

/** Une chiquenaude bascule sans aller au bout de la course (px/ms). */
const FLICK_VELOCITY = 0.45;

/** Course minimale pour qu'une chiquenaude compte — sinon un tap suffirait. */
const FLICK_MIN_PX = 24;

/** Durée du recalage, à l'aller comme au retour. */
export const SETTLE_MS = 260;

/**
 * Filet : au-delà, on recale même si la navigation n'a pas abouti.
 *
 * ⚠️ Ne devrait jamais servir — une navigation locale aboutit en une poignée de
 * millisecondes. Mais rester bloqué sur le panneau voisin serait bien pire que
 * l'image de trop qu'on cherche justement à supprimer : un écran figé, sans rien
 * pour l'expliquer.
 */
const COMMIT_TIMEOUT_MS = 1000;

/** Gouttière entre deux journées, en pixels. */
export const PANE_GAP_PX = 16;

export type PagerDirection = 'prev' | 'next';

interface Gesture {
  pointerId: number;
  x: number;
  y: number;
  axis: 'unknown' | 'x' | 'y';
  /**
   * Largeur de la piste, relevée au verrouillage de l'axe.
   *
   * ⚠️ Relevée sur la cible de l'événement plutôt que par une `ref` : renvoyer
   * une ref depuis ce hook faisait prendre tout l'objet retourné pour une ref
   * par `react-hooks/refs`, et lire `pager.offset` pendant le rendu devenait une
   * erreur de lint. Ici il n'y a plus de ref du tout.
   */
  width: number;
  /** Avant-dernière position, pour estimer la vitesse au relâchement. */
  prevX: number;
  prevAt: number;
  lastX: number;
  lastAt: number;
  peeked: PagerDirection | null;
}

export interface DayPager {
  /** Décalage courant de la piste, en pixels. */
  offset: number;
  /** Journée voisine à monter — `null` quand rien ne dépasse. */
  peek: PagerDirection | null;
  /** Vrai pendant le recalage : c'est le seul moment où une transition CSS joue. */
  animating: boolean;
  /**
   * Vrai quand la journée affichée vient d'un **glissement** — cf. le
   * commentaire du `return`. Les flèches ne le lèvent jamais.
   */
  swiped: boolean;
  handlers: {
    onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerCancel: () => void;
    onDragStart: (event: ReactDragEvent<HTMLDivElement>) => void;
  };
}

export function useDayPager({
  date,
  onCommit,
  onPeek,
}: {
  /**
   * Date **actuellement demandée par la route**, et non celle des données.
   *
   * 🔴 C'est le signal qui dit « la navigation a abouti » — cf. `settle`. La
   * date des données ne conviendrait pas : elle arrive plus tard si la journée
   * n'était pas encore en cache, et le recalage se ferait attendre pour rien.
   */
  date: string;
  /** Le glissement est allé au bout : changer de journée. */
  onCommit: (direction: PagerDirection) => void;
  /** L'utilisateur commence à découvrir cette journée : la charger. */
  onPeek: (direction: PagerDirection) => void;
}): DayPager {
  const gesture = useRef<Gesture | null>(null);
  const timer = useRef<number | null>(null);

  const [offset, setOffset] = useState(0);
  const [peek, setPeek] = useState<PagerDirection | null>(null);
  const [animating, setAnimating] = useState(false);
  /** Date quittée par un glissement abouti, tant que la nouvelle n'est pas là. */
  const [pending, setPending] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  /**
   * Recale la piste, puis — si le glissement a abouti — change de journée.
   *
   * ══════════════════════════════════════════════════════════════════════════
   * 🔴 ON NE RECALE PAS AVANT QUE LA NOUVELLE JOURNÉE SOIT LÀ
   * ══════════════════════════════════════════════════════════════════════════
   *
   * Ce commentaire affirmait que « la bascule et la remise à zéro du décalage
   * sont faites dans le même lot de rendu ». **C'était faux**, et mesuré comme
   * tel le 29/08/2026 : un banc échantillonnant à chaque `requestAnimationFrame`
   * a trouvé **une image** — une seule, ~19 ms — où la piste était déjà recalée
   * (`tx = 0`), le panneau voisin déjà démonté, et l'en-tête affichant **encore
   * l'ancienne date**. Autrement dit l'ancienne journée repeinte plein écran,
   * entre la nouvelle (vue de côté) et la nouvelle (vue au centre). C'est
   * exactement ce qu'on percevait comme un « rechargement ».
   *
   * La cause : `navigate()` de react-router **n'est pas synchrone**. Le
   * `setOffset(0)` et le `setPeek(null)` partent tout de suite, la navigation
   * arrive un tour plus tard. Deux mises à jour d'état, deux lots de rendu, une
   * image entre les deux.
   *
   * Le remède n'est pas de rendre la navigation synchrone — on ne peut pas —
   * mais **d'attendre qu'elle ait abouti** pour recaler : la piste reste
   * déplacée, donc le panneau voisin occupe l'écran, jusqu'à ce que `date`
   * change. La journée qui arrive alors au centre remplace au pixel près celle
   * qu'on regardait de côté.
   *
   * ⚠️ **Le recalage est DÉRIVÉ au rendu** (cf. `aboutie` plus bas), pas posé
   * dans un effet. Un `useLayoutEffect` marcherait aussi, mais il ferait un
   * rendu de plus pour une valeur que le rendu courant sait déjà calculer — et
   * `react-hooks/set-state-in-effect` le refuse, à juste titre.
   *
   * ⚠️ **`animating` retombe ici, avant le recalage.** Sans quoi le retour de
   * ±largeur à zéro s'animerait : on verrait la nouvelle journée *glisser*
   * depuis le côté après y être déjà arrivée.
   */
  const settle = useCallback(
    (target: number, direction: PagerDirection | null) => {
      setAnimating(true);
      setOffset(target);
      timer.current = window.setTimeout(() => {
        timer.current = null;
        // La transition a fini son office : plus rien ne doit s'animer, que ce
        // soit le retour à zéro ci-dessous ou celui du recalage différé.
        setAnimating(false);
        if (direction) {
          setPending(date);
          onCommit(direction);
        } else {
          setOffset(0);
          setPeek(null);
        }
      }, SETTLE_MS);
    },
    /*
     * ⚠️ `date` en dépendance plutôt qu'une `ref` lue au rendu : écrire une ref
     * pendant le rendu est refusé par `react-hooks/refs`, et à raison — la
     * valeur lue dépendrait alors de l'ordre des rendus. `settle` n'est appelée
     * qu'au relâchement du doigt, donc se recréer à chaque changement de date
     * ne coûte rien.
     */
    [onCommit, date],
  );

  /**
   * La navigation a-t-elle abouti ? — **dérivé, jamais posé dans un effet.**
   *
   * 🔴 Un `useLayoutEffect` qui appellerait `setOffset(0)` marcherait, et
   * `react-hooks/set-state-in-effect` le refuse à juste titre : ce serait un
   * rendu de plus pour une valeur que le rendu courant sait déjà calculer. Ici
   * la piste est **rendue** au centre dès l'image où `date` change — il n'y a
   * aucun instant intermédiaire à peindre, donc rien à corriger après coup.
   *
   * ⚠️ L'état `offset` / `peek` reste sur ses anciennes valeurs pendant ce
   * temps ; c'est sans conséquence puisque plus personne ne les lit, et le
   * prochain geste les remet à zéro (cf. `onPointerDown`).
   */
  const aboutie = pending !== null && date !== pending;

  /** Filet — cf. `COMMIT_TIMEOUT_MS`. */
  useEffect(() => {
    if (pending === null) return;
    const filet = window.setTimeout(() => {
      setPending(null);
      setOffset(0);
      setPeek(null);
    }, COMMIT_TIMEOUT_MS);
    return () => {
      window.clearTimeout(filet);
    };
  }, [pending]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      // Pendant un recalage, la piste ne s'attrape pas : on la laisse finir.
      if (timer.current !== null) return;

      if (pending !== null) {
        /*
         * Une bascule est en cours. Si la navigation n'a pas encore abouti, on
         * ne touche à rien — la piste montre la journée qui arrive. Si elle a
         * abouti, c'est ici qu'on solde l'état laissé derrière : le prochain
         * geste doit partir d'une piste propre.
         */
        if (!aboutie) return;
        setPending(null);
        setOffset(0);
        setPeek(null);
      }

      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // Bande de retour natif — cf. avertissement en tête de fichier.
      if (event.clientX <= EDGE_GUARD_PX) return;

      const now = performance.now();
      gesture.current = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        axis: 'unknown',
        width: event.currentTarget.clientWidth,
        prevX: event.clientX,
        prevAt: now,
        lastX: event.clientX,
        lastAt: now,
        peeked: null,
      };
    },
    [pending, aboutie],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const g = gesture.current;
      if (!g || g.pointerId !== event.pointerId) return;

      const dx = event.clientX - g.x;
      const dy = event.clientY - g.y;

      if (g.axis === 'unknown') {
        if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
        if (Math.abs(dy) >= Math.abs(dx)) {
          // Défilement : on se retire du geste pour de bon.
          gesture.current = null;
          return;
        }
        g.axis = 'x';
        // Capture : le doigt peut sortir de la piste sans casser le suivi.
        event.currentTarget.setPointerCapture(event.pointerId);
      }

      g.prevX = g.lastX;
      g.prevAt = g.lastAt;
      g.lastX = event.clientX;
      g.lastAt = performance.now();

      const direction: PagerDirection = dx > 0 ? 'prev' : 'next';
      if (Math.abs(dx) > PEEK_PX && g.peeked !== direction) {
        g.peeked = direction;
        onPeek(direction);
      }

      setPeek(dx === 0 ? null : direction);
      setOffset(dx);
    },
    [onPeek],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const g = gesture.current;
      gesture.current = null;
      if (!g || g.axis !== 'x') {
        setOffset(0);
        setPeek(null);
        return;
      }

      const width = g.width > 0 ? g.width : window.innerWidth;
      const dx = event.clientX - g.x;
      // Plancher de 8 ms : deux événements dans la même frame donneraient une
      // vitesse absurde et feraient basculer sur un simple frémissement.
      const elapsed = Math.max(8, performance.now() - g.prevAt);
      const velocity = (event.clientX - g.prevX) / elapsed;

      const farEnough = Math.abs(dx) > width * COMMIT_RATIO;
      const flicked =
        Math.abs(velocity) > FLICK_VELOCITY &&
        Math.abs(dx) > FLICK_MIN_PX &&
        Math.sign(velocity) === Math.sign(dx);

      if (farEnough || flicked) {
        const direction: PagerDirection = dx > 0 ? 'prev' : 'next';
        const full = width + PANE_GAP_PX;
        settle(direction === 'prev' ? full : -full, direction);
      } else {
        settle(0, null);
      }
    },
    [settle],
  );

  const onPointerCancel = useCallback(() => {
    const g = gesture.current;
    gesture.current = null;
    if (g?.axis === 'x') settle(0, null);
    else {
      setOffset(0);
      setPeek(null);
    }
  }, [settle]);

  /**
   * Deuxième verrou contre le glisser-déposer natif, après `user-select: none`.
   * Le premier empêche la sélection de naître ; celui-ci couvre le cas où elle
   * existe déjà — un `dragstart` non annulé fait suivre un `pointercancel` qui
   * coupe le geste au milieu.
   */
  const onDragStart = useCallback((event: ReactDragEvent<HTMLDivElement>) => {
    event.preventDefault();
  }, []);

  return {
    /*
     * 🔴 **Dérivés, pas lus tels quels.** Dès que la navigation a abouti, la
     * piste est rendue centrée et le panneau voisin démonté — dans **le même
     * rendu** que la nouvelle date. C'est ce qui supprime l'image intermédiaire
     * mesurée le 29/08 : plus aucune frame ne peint l'ancienne journée au
     * centre. Voir `settle` pour le raisonnement complet.
     */
    offset: aboutie ? 0 : offset,
    peek: aboutie ? null : peek,
    animating,
    /**
     * 🔴 **La journée affichée vient d'un GLISSEMENT** — demandé après usage
     * *(31/08/2026)* : « que l'animation de remplissage ne s'applique que si je
     * change de jour avec les deux boutons ».
     *
     * L'anneau de calories et les barres de macros s'animent par une transition
     * CSS. Or `goTo()` sert **aux deux** chemins — les flèches et le pager — et
     * navigue vers la même route : `MacroBanner` n'est donc jamais démonté, ses
     * valeurs changent sur un élément qui persiste, et la transition rejoue. Au
     * glissement, elle arrive **par-dessus** un mouvement déjà en cours, ce qui
     * fait deux animations pour un seul geste.
     *
     * ⚠️ **`aboutie` est déjà exactement ce signal**, et c'est pourquoi rien
     * n'est ajouté : il est vrai à partir du rendu où la route a changé **après
     * un glissement**, c'est-à-dire au rendu même où les valeurs changent. Une
     * navigation par les flèches ne le lève jamais.
     *
     * ⚠️ **Il retombe tout seul** — au prochain contact sur la piste
     * (`onPointerDown` solde l'état) ou par le filet, à 1 s. Il n'y a donc pas
     * d'état « bloqué » à réarmer : la fenêtre sans animation est bornée, et
     * elle se referme dès qu'on touche l'écran, c'est-à-dire avant toute
     * saisie. C'est ce qui a fait préférer ce signal à un drapeau posé à la
     * main, qu'il aurait fallu remettre à zéro quelque part.
     */
    swiped: aboutie,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onDragStart },
  };
}
